import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { useChat } from '../contexts/ChatContext';
import { useWindows } from '../contexts/WindowContext';
import DockedChatPanel from './DockedChatPanel';

const SIDEBAR_WIDTH = 360;
const WORKSPACE_WINDOW_ID = 'chat_workspace_main';

const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDockedChat = (conversation) => {
  if (!conversation?.conversationId) return null;

  const isDirect = conversation.type === 'direct';
  const other = conversation.otherUser || {};

  if (isDirect) {
    return {
      conversationId: conversation.conversationId,
      conversationType: 'direct',
      userUid: other.userUid || null,
      username: other.username || other.displayName || 'unknown',
      displayName: other.displayName || other.username || '알 수 없는 사용자',
      role: other.role || 'USER',
      title: other.displayName || other.username || '알 수 없는 사용자',
      subtitle: other.displayName && other.displayName !== other.username
        ? `@${other.username}`
        : (other.role || '1:1 채팅'),
    };
  }

  const title = conversation.title || '그룹 채팅';
  const participantCount = Array.isArray(conversation.participantUids)
    ? conversation.participantUids.length
    : 0;

	  return {
	    conversationId: conversation.conversationId,
	    conversationType: 'group',
	    userUid: null,
	    username: title,
    displayName: title,
	    role: 'GROUP',
	    title,
	    subtitle: participantCount > 0 ? `참여자 ${participantCount}명` : '그룹 채팅',
	    ownerUid: conversation.ownerUid || conversation.createdByUid || '',
	    coHostUids: conversation.coHostUids || [],
	    participantUids: conversation.participantUids || [],
	    pendingInviteUids: conversation.pendingInviteUids || [],
	    participants: conversation.participants || [],
	    pendingInvites: conversation.pendingInvites || [],
	    viewerRole: conversation.viewerRole || 'member',
	    viewerCanManage: !!conversation.viewerCanManage,
	    viewerCanDelete: !!conversation.viewerCanDelete,
	  };
	};

const ChatRoomSidebar = ({
  open,
  onClose,
  activeChat = null,
  onActiveChatChange = () => {},
}) => {
  const theme = useTheme();
  const {
    conversations,
    loadConversations,
    loadingConversations,
    createGroupConversation,
    respondConversationInvite,
  } = useChat();
  const { openWindows, setOpenWindows, topZIndex, setTopZIndex, focusWindow } = useWindows();
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [roomTitle, setRoomTitle] = useState('');
  const [inviteText, setInviteText] = useState('');
  const [friendOptions, setFriendOptions] = useState([]);
  const [selectedInviteUids, setSelectedInviteUids] = useState([]);
  const [creatingRoom, setCreatingRoom] = useState(false);

  useEffect(() => {
    if (!open) return;
    loadConversations();
  }, [open, loadConversations]);

  useEffect(() => {
    if (!createOpen) return;
    let cancelled = false;
    axios.get('/api/friends/sidebar', { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setFriendOptions(Array.isArray(res.data?.friends) ? res.data.friends : []);
      })
      .catch(() => {
        if (!cancelled) setFriendOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [createOpen]);


  const filteredConversations = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;

    return conversations.filter((conversation) => {
      const other = conversation.otherUser || {};
      const title =
        conversation.type === 'direct'
          ? (other.displayName || other.username || '')
          : (conversation.title || '그룹 채팅');

      return [
        title,
        other.username,
        other.displayName,
        conversation.lastMessagePreview,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [conversations, query]);

  const handleConversationClick = (conversation) => {
    if (conversation?.inviteStatus === 'PENDING') return;
    const next = toDockedChat(conversation);
    if (!next) return;

    const workspace = openWindows.find(
      (w) => w.id === WORKSPACE_WINDOW_ID && w.winType === 'chat' && w.chatMode === 'workspace'
    );

    if (workspace) {
      setOpenWindows((prev) =>
        prev.map((w) =>
          w.id === WORKSPACE_WINDOW_ID
            ? {
                ...w,
                workspaceSelectedConversationId: next.conversationId,
                isMinimized: false,
              }
            : w
        )
      );
      onActiveChatChange(null);
      focusWindow(WORKSPACE_WINDOW_ID);
      onClose?.();
      return;
    }

    onActiveChatChange(
      activeChat?.conversationId === next.conversationId ? null : next
    );
  };

  const openWorkspaceWindow = (conversationId = null) => {
    const existing = openWindows.find(
      (w) => w.id === WORKSPACE_WINDOW_ID && w.winType === 'chat' && w.chatMode === 'workspace'
    );

    if (existing) {
      setOpenWindows((prev) =>
        prev.map((w) =>
          w.id === WORKSPACE_WINDOW_ID
            ? {
                ...w,
                workspaceSelectedConversationId:
                  conversationId || w.workspaceSelectedConversationId || null,
                isMinimized: false,
              }
            : w
        )
      );
      focusWindow(WORKSPACE_WINDOW_ID);
      onClose?.();
      return;
    }

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
    const workspaceWidth = Math.max(480, Math.min(980, viewportWidth - 64));
    const workspaceHeight = Math.max(420, Math.min(680, viewportHeight - 120));
    const workspaceX = Math.max(24, Math.round((viewportWidth - workspaceWidth) / 2));
    const workspaceY = Math.max(24, Math.min(72, Math.round((viewportHeight - workspaceHeight) / 2)));

    setOpenWindows((prev) => [
      ...prev,
      {
        id: WORKSPACE_WINDOW_ID,
        name: '채팅 관리',
        winType: 'chat',
        chatMode: 'workspace',
        chatUsername: '채팅 관리',
        chatDisplayName: '채팅 관리',
        workspaceSelectedConversationId: conversationId || activeChat?.conversationId || null,
        width: workspaceWidth,
        height: workspaceHeight,
        x: workspaceX,
        y: workspaceY,
        zIndex: topZIndex + 1,
        isMinimized: false,
        isMaximized: false,
      },
    ]);
    setTopZIndex((prev) => prev + 1);
    setTimeout(() => focusWindow(WORKSPACE_WINDOW_ID), 0);
    onClose?.();
  };

  const resetCreateDialog = () => {
    setRoomTitle('');
    setInviteText('');
    setSelectedInviteUids([]);
    setCreatingRoom(false);
  };

  const handleCloseCreateDialog = () => {
    setCreateOpen(false);
    resetCreateDialog();
  };

  const handleToggleInviteUid = (userUid) => {
    setSelectedInviteUids((prev) =>
      prev.includes(userUid)
        ? prev.filter((uid) => uid !== userUid)
        : [...prev, userUid]
    );
  };

  const parseTypedInvitees = () =>
    inviteText
      .split(/[,\n]/)
      .map((value) => value.trim())
      .filter(Boolean);

  const handleCreateRoom = async () => {
    const typedInvitees = parseTypedInvitees();
    if (selectedInviteUids.length === 0 && typedInvitees.length === 0) {
      alert('초대할 친구나 아이디를 하나 이상 입력하세요.');
      return;
    }

    setCreatingRoom(true);
    try {
      const conversation = await createGroupConversation({
        title: roomTitle || '그룹 채팅',
        inviteeUids: selectedInviteUids,
        invitees: typedInvitees,
      });
      await loadConversations({ silent: true });
      handleCloseCreateDialog();
      if (conversation?.conversationId) {
        openWorkspaceWindow(conversation.conversationId);
      }
    } catch (err) {
      alert(err.response?.data?.error || '채팅방 생성에 실패했습니다.');
      setCreatingRoom(false);
    }
  };

  const handleRespondInvite = async (conversation, accept) => {
    try {
      const updated = await respondConversationInvite(conversation.conversationId, accept);
      await loadConversations({ silent: true });
      if (accept && updated?.conversationId) {
        onActiveChatChange(toDockedChat(updated));
      }
    } catch (err) {
      alert(err.response?.data?.error || '초대 응답에 실패했습니다.');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '88vw', sm: SIDEBAR_WIDTH },
          height: 'var(--app-viewport-height)',
          maxHeight: 'var(--app-viewport-height)',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
        <DockedChatPanel
          sidebarWidth={SIDEBAR_WIDTH}
          activeChat={activeChat}
          onOpenWindow={() => openWorkspaceWindow(activeChat?.conversationId || null)}
          onConversationReady={(conversation) => {
            if (!conversation?.conversationId) return;
            onActiveChatChange(toDockedChat(conversation));
          }}
          onCloseChat={() => onActiveChatChange(null)}
        />

        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'background.default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatBubbleOutlineIcon fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
              채팅방
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setCreateOpen(true)} title="채팅방 만들기">
              <AddIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => openWorkspaceWindow(activeChat?.conversationId || null)}
            >
              window
            </Button>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="채팅방 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              size="small"
              color="primary"
              onClick={() => setCreateOpen(true)}
              title="채팅방 만들기"
              sx={{ border: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            채팅방을 누르면 이 목록의 왼쪽에 채팅방 창이 열립니다.
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.25, position: 'relative', zIndex: 1 }}>
          {loadingConversations ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Typography variant="body2" sx={{ px: 1, py: 1, color: 'text.secondary' }}>
              표시할 채팅방이 없습니다.
            </Typography>
          ) : (
            <List disablePadding>
              {filteredConversations.map((conversation, idx) => {
                const other = conversation.otherUser || {};
                const isDirect = conversation.type === 'direct';
                const title = isDirect
                  ? (other.displayName || other.username || '알 수 없는 사용자')
                  : (conversation.title || '그룹 채팅');
                const subtitle = conversation.lastMessagePreview || '아직 메시지가 없습니다.';
                const unreadCount = Number(conversation.unreadCount) || 0;
                const selected = activeChat?.conversationId === conversation.conversationId;
                const pendingInvite = conversation.inviteStatus === 'PENDING';

                return (
                  <React.Fragment key={conversation.conversationId}>
                    <ListItemButton
                      onClick={() => handleConversationClick(conversation)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        alignItems: 'flex-start',
                        backgroundColor: selected ? 'action.selected' : 'transparent',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: isDirect ? 'primary.main' : 'secondary.main',
                            fontWeight: 'bold',
                          }}
                        >
                          {isDirect ? (
                            (title?.[0] || 'D').toUpperCase()
                          ) : (
                            <GroupIcon sx={{ fontSize: 20 }} />
                          )}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                              {title}
                            </Typography>
                            <Chip
                              size="small"
                              color={isDirect ? 'primary' : 'secondary'}
                              variant="outlined"
                              icon={isDirect ? <PersonIcon /> : <GroupIcon />}
                              label={isDirect ? '1:1' : '그룹'}
                              sx={{ height: 20 }}
                            />
                            {unreadCount > 0 && (
                              <Chip
                                size="small"
                                color="error"
                                label={unreadCount > 99 ? '99+' : unreadCount}
                                sx={{ height: 20 }}
                              />
                            )}
                            {pendingInvite && (
                              <Chip
                                size="small"
                                color="warning"
                                label="초대"
                                sx={{ height: 20 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 220,
                              }}
                            >
                              {subtitle}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                              {formatTime(conversation.lastMessageAt || conversation.updatedAt || conversation.createdAt)}
                            </Typography>
                            {pendingInvite && (
                              <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRespondInvite(conversation, true);
                                  }}
                                >
                                  수락
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRespondInvite(conversation, false);
                                  }}
                                >
                                  거절
                                </Button>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                    {idx < filteredConversations.length - 1 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        <Dialog open={createOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="xs">
          <DialogTitle>채팅방 만들기</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="채팅방 이름"
              value={roomTitle}
              onChange={(event) => setRoomTitle(event.target.value)}
              placeholder="예: 프로젝트 회의"
              fullWidth
              size="small"
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                친구 초대
              </Typography>
              <Box sx={{ maxHeight: 170, overflowY: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
                {friendOptions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">표시할 친구가 없습니다.</Typography>
                ) : (
                  friendOptions.map((friend) => (
                    <FormControlLabel
                      key={friend.userUid}
                      control={
                        <Checkbox
                          checked={selectedInviteUids.includes(friend.userUid)}
                          onChange={() => handleToggleInviteUid(friend.userUid)}
                        />
                      }
                      label={`${friend.displayName || friend.username} (${friend.username})`}
                      sx={{ display: 'flex', mr: 0 }}
                    />
                  ))
                )}
              </Box>
            </Box>
            <TextField
              label="아이디 또는 닉네임으로 초대"
              value={inviteText}
              onChange={(event) => setInviteText(event.target.value)}
              placeholder="쉼표 또는 줄바꿈으로 여러 명 입력"
              fullWidth
              multiline
              minRows={2}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              초대받은 사용자가 수락해야 채팅방과 연결된 화상회의에 들어올 수 있습니다.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>취소</Button>
            <Button variant="contained" onClick={handleCreateRoom} disabled={creatingRoom}>
              만들기
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
  );
};

export default ChatRoomSidebar;
